import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { hapticSelection } from '../utils/haptics';

// Authentic 3-Wheeler Auto-Rickshaw SVG Icon matching user design
const BookIcon = ({ active }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 100 100"
    fill="none"
    stroke="currentColor"
    strokeWidth={active ? "8" : "6.5"}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Outer Cabin Profile & Roof Overhang */}
    <path d="M88 15H18C10 15 4 21 4 29V66C4 70 7 73 11 73H13" />
    {/* Underbody line between wheels */}
    <path d="M29 73H72" />
    {/* Rear window & stripe */}
    <path d="M34 18V36H6" />
    <path d="M6 46H34" />
    {/* Passenger entryway cut */}
    <path d="M38 18V46C38 52 42 56 48 56H64V18" />
    {/* Front windshield */}
    <path d="M68 18V44H84L78 18H68Z" />
    {/* Front A-pillar & mudguard */}
    <path d="M78 18L86 48V58H68" />
    <path d="M86 58L97 68H78L86 58Z" />
    {/* Wheels */}
    <circle cx="21" cy="76" r="9" strokeWidth={active ? "8" : "6.5"} fill={active ? "currentColor" : "none"} />
    <circle cx="87" cy="78" r="9" strokeWidth={active ? "8" : "6.5"} fill={active ? "currentColor" : "none"} />
  </svg>
);

const RidesIcon = ({ active }) => active ? (
  /* Filled clock/history */
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm.5-13a.5.5 0 0 0-1 0v5.25l3.5 2.1a.5.5 0 0 0 .5-.87L12.5 11.5V7z"/>
  </svg>
) : (
  /* Outline clock */
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9"/>
    <polyline points="12 7 12 12 15.5 14.5"/>
  </svg>
);

const IntertownIcon = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 18c3-4.5 4.5-8.5 7.5-8.5S16 13.5 20 6" />
    <path d="M4 6h3M17 18h3" opacity=".7" />
    <circle cx="4" cy="18" r="1.4" fill={active ? '#fff' : 'currentColor'} />
    <circle cx="20" cy="6" r="1.4" fill={active ? '#fff' : 'currentColor'} />
  </svg>
);

const ProfileIcon = ({ active }) => active ? (
  /* Filled person */
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z"/>
  </svg>
) : (
  /* Outline person */
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="8" r="4"/>
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);

export default function BottomNav() {
  const { t } = useLanguage();
  const { pathname } = useLocation();

  if (pathname.startsWith('/login') || pathname.startsWith('/active') || pathname.startsWith('/track')) {
    return null;
  }

  const items = [
    { to: '/', Icon: BookIcon, label: t('nav.home') || 'Book', id: 'book' },
    { to: '/intertown', Icon: IntertownIcon, label: t('nav.intertown') || 'Intertown', id: 'intertown' },
    { to: '/rides', Icon: RidesIcon, label: t('nav.rides') || 'Rides', id: 'rides' },
    { to: '/profile', Icon: ProfileIcon, label: t('nav.profile') || 'Profile', id: 'profile' },
  ];

  let activeIndex = 0;
  if (pathname.startsWith('/intertown')) activeIndex = 1;
  else if (pathname.startsWith('/rides')) activeIndex = 2;
  else if (pathname.startsWith('/profile')) activeIndex = 3;

  return (
    <div className="bottom-nav-outer-wrap">
      <nav className="bottom-nav solid-docked" aria-label={t('nav.main') || 'Main'}>
        {/* Animated Sliding Pill Glider */}
        <div
          className="nav-glider-pill"
          style={{
            transform: `translateX(${activeIndex * 100}%)`,
          }}
          aria-hidden="true"
        />

        {items.map((item, idx) => {
          const isActive = activeIndex === idx;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={`nav-item ${item.id}${isActive ? ' active' : ''}`}
              onClick={() => {
                if (!isActive) hapticSelection();
              }}
            >
              <div className={`nav-icon-wrap ${item.id}${isActive ? ' animated-bounce' : ''}`}>
                <item.Icon active={isActive} />
              </div>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
